import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { isArray } from '@/se/utilities/check';

const asArray = object => (isArray(object) ? object : [object]);

const DIRECTION = {
  LEFT: -1,
  RIGHT: 1,
};

const Container = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex: 1 0 auto;
  position: relative;
  width: ${props => props.panels * 100}%;
`;

const Panel = styled.div`
  opacity: ${props => (props.visible ? 1 : 0)};
  transform: translateX(${props => `${(props.idx || 0) * -100 + (props.visible ? 0 : (props.direction || 1) * 100)}%`});
  transition: 400ms all ease-in;
  transition-delay: 100ms;
  overflow: hidden;
  flex: 1;

  > * {
    margin: 0.25rem 0;
  }
`;

function slideCount(items, size) {
  return Math.ceil(items.length / size);
}

function partition(items = [], size) {
  const count = slideCount(items, size);
  return [...Array(count)].map((_, idx) => items.slice(idx * size, idx * size + size));
}

const Carousel = React.memo(function Carousel({ children, size, interval }) {
  const items = asArray(children);
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef(null);
  const mountedRef = useRef(false);

  const panels = useMemo(() => partition(items, size), [items, size]);
  const count = useMemo(() => slideCount(items, size), [items, size]);

  const clear = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clear();
    const ms = Math.max(500, interval || 7000);
    intervalRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      if (count <= 1) return;
      setCurrent(prev => (prev + 1) % count);
    }, ms);
  }, [clear, interval, count]);

  useEffect(() => {
    mountedRef.current = true;
    const onVisibility = () => {
      if (document.hidden) {
        clear();
      } else {
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    start();
    return () => {
      mountedRef.current = false;
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [start, clear]);

  useEffect(() => {
    setCurrent(prev => (count === 0 ? 0 : prev % count));
    start();
    return () => clear();
  }, [count, size, items.length, interval, start, clear]);

  return (
    <Container panels={count}>
      {panels.map((panel, idx) => (
        <Panel
          idx={idx}
          visible={idx === current}
          direction={
            idx - current === 1 || (current - idx === panels.length - 1 && panels.length > 2)
              ? DIRECTION.LEFT
              : DIRECTION.RIGHT
          }
          key={idx}
        >
          {panel}
        </Panel>
      ))}
    </Container>
  );
});

export default Carousel;
